import React from 'react'
import { graphql, Link } from 'gatsby'
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews'
import { format, parseISO } from 'date-fns'
import { PrismicRichText } from '@prismicio/react'
import { GatsbyImage } from 'gatsby-plugin-image'

// Components
import Layout from '../components/layout'
import Seo from '../components/seo'

export const query = graphql`
  query BlogPost($skip: Int!, $limit: Int!) {
    prismicPage(uid: { eq: "blog" }) {
      ...PrismicPageFragment
    }
    allPrismicBlogpost(limit: $limit, skip: $skip, sort: { first_publication_date: DESC }) {
      edges {
        node {
          ...PrismicBlogpostFragment
        }
      }
    }
  }
`

const BlogTemplate = ({ data, location, pageContext }) => {
  const page = data.prismicPage
  const pageData = page.data
  const blogPosts = data.allPrismicBlogpost.edges

  const { currentPage, numPages } = pageContext
  const isFirst = currentPage === 1
  const isLast = currentPage === numPages
  const pageLink = '/blog/'
  const prevPage = currentPage - 1 === 1 ? pageLink : pageLink + (currentPage - 1).toString() + '/'
  const nextPage = pageLink + (currentPage + 1).toString() + '/'

  return (
    <Layout location={location}>
      <div className='text-lg pb-6'>
        <div className='relative bg-gradient-to-tr from-fuchsia-800 via-indigo-800 to-teal-600'>
          <div className='pt-32 pb-12 sm:pt-40 sm:pb-24 lg:pt-48 px-10 flex flex-col justify-center z-10 text-center text-white max-w-6xl mx-auto'>
            <div className='tracking-tight text-4xl sm:text-5xl md:text-6xl drop-shadow'>
              <PrismicRichText field={pageData.page_title.richText} />
            </div>
          </div>
        </div>
        <div className='max-w-7xl mx-auto relative px-4 sm:px-6 pb-4 sm:pb-8 py-5 md:py-8'>
          <div className='relative'>
            <ul className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
              {blogPosts.map((blogpost, index) => {
                const blogpostData = blogpost.node.data

                const parseDate = parseISO(blogpost.node.first_publication_date)
                const formattedDate = format(new Date(parseDate), 'EEEE, do MMMM yyyy')

                const timeToRead = Math.ceil(blogpostData.blogpost_text.text.split(' ').length / 200)
                const featured = blogpostData.featured || false

                return (
                  <li
                    key={`blogpost-${index}`}
                    className={`component-card flex flex-col shadow-lg overflow-hidden border border-gray-50 dark:border-gray-900 relative transform transition duration-300 ease hover:scale-105 hover:dark:shadow-purple-300/10 hover:shadow-lg ${
                      featured ? 'sm:col-span-2 bg-indigo-800' : 'bg-white dark:bg-gray-700'
                    }`}>
                    <Link className='flex flex-col flex-1 p-2' to={blogpost.node.url}>
                      {featured && (
                        <div className='absolute z-10 top-1 left-1 leading-6 tracking-wider py-0.5 px-1.5 rounded-sm self-start uppercase bg-indigo-600 text-purple-50'>featured article</div>
                      )}
                      {blogpostData.blogpost_image.gatsbyImageData && (
                        <div className='flex-shrink-0 relative'>
                          <GatsbyImage image={blogpostData.blogpost_image.gatsbyImageData} alt={blogpostData.blogpost_image.alt || ''} loading='eager' />
                          {blogpostData.blogpost_image.copyright && (
                            <span className='absolute bottom-0 right-0 text-white bg-black opacity-50 m-2 px-2 py-1 text-xs rounded-sm'>{blogpostData.blogpost_image.copyright}</span>
                          )}
                        </div>
                      )}

                      <div className='flex-1 m-3 flex flex-col'>
                        <div className='space-y-4 mb-6'>
                          <div className={`${featured && 'prose-invert'} mt-3 mb-6 prose dark:prose-invert min-w-full`}>
                            <h2>{blogpostData.blogpost_title.text}</h2>
                            <p>{blogpostData.blogpost_text.text.split(' ').slice(0, 30).join(' ')} &hellip;</p>
                          </div>
                        </div>
                        {blogpostData.author[0] && (
                          <div className='mt-auto flex items-center'>
                            <div className='flex-shrink-0 relative'>
                              <GatsbyImage
                                image={blogpostData.author[0].author_link.document.data.page_image.gatsbyImageData}
                                className='h-10 w-10 rounded-lg rounded-bl-none border-1 border-teal-600'
                                alt={blogpostData.author[0].author_link.document.data.page_image.alt || ''}
                                loading='eager'
                              />
                            </div>
                            <div className={`ml-3 ${featured ? 'text-white' : 'text-gray-900'}`}>
                              <p className='text-sm font-semibold dark:text-gray-50'>{blogpostData.author[0].author_link.document.data.page_title.text}</p>
                              <div className='flex space-x-1 text-sm opacity-90 dark:text-gray-200'>
                                {formattedDate} &middot; {timeToRead} min read
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </Link>
                  </li>
                )
              })}
            </ul>
          </div>
          <nav className='mx-auto text-center mt-10'>
            {!isFirst && (
              <Link
                className='relative inline-flex items-center px-4 py-2 mx-1 shadow-md rounded-md bg-white dark:border-gray-700 dark:bg-gray-800 dark:text-gray-50 hover:dark:text-indigo-500 text-sm text-gray-700 hover:bg-indigo-100'
                to={prevPage}
                rel='prev'>
                ← Prev
              </Link>
            )}
            {currentPage >= 3 && (
              <Link
                className='relative inline-flex items-center px-4 py-2 mx-1 shadow-md rounded-md bg-white dark:border-gray-700 dark:bg-gray-800 dark:text-gray-50 hover:dark:text-indigo-500 text-sm text-gray-700 hover:bg-indigo-100'
                activeClassName='bg-indigo-100'
                to={pageLink}>
                1
              </Link>
            )}
            {currentPage >= 3 && <li className='relative inline-flex items-center px-4 py-2 text-sm text-gray-700'>&hellip;</li>}
            {numPages > 1 &&
              Array.from({ length: numPages }, (_, i) => {
                if (i < currentPage + 1 && i > currentPage - 3) {
                  return (
                    <Link
                      key={`pagination-${i}`}
                      className='relative inline-flex items-center px-4 py-2 mx-1 shadow-md rounded-md bg-white dark:border-gray-700 dark:bg-gray-800 dark:text-gray-50 hover:dark:text-indigo-500 text-sm text-gray-700 hover:bg-indigo-100'
                      activeClassName='bg-indigo-100'
                      to={`${pageLink}${i === 0 ? '' : i + 1 + '/'}`}>
                      {i + 1 < 10 ? `${i + 1}` : i + 1}
                    </Link>
                  )
                }
                return null
              })}
            {currentPage + 1 < numPages && <li className='relative inline-flex items-center px-4 py-2 text-sm text-gray-700'>&hellip;</li>}
            {currentPage + 1 < numPages && (
              <Link
                className='relative inline-flex items-center px-4 py-2 mx-1 shadow-md rounded-md bg-white dark:border-gray-700 dark:bg-gray-800 dark:text-gray-50 hover:dark:text-indigo-500 text-sm text-gray-700 hover:bg-indigo-100'
                to={`${pageLink}${numPages}/`}>
                {numPages < 10 ? `${numPages}` : numPages}
              </Link>
            )}
            {!isLast && (
              <Link
                className='relative inline-flex items-center px-4 py-2 mx-1 shadow-md rounded-md bg-white dark:border-gray-700 dark:bg-gray-800 dark:text-gray-50 hover:dark:text-indigo-500 text-sm text-gray-700 hover:bg-indigo-100'
                to={nextPage}
                rel='next'>
                Next →
              </Link>
            )}
          </nav>
        </div>
      </div>
    </Layout>
  )
}

export default withPrismicPreview(BlogTemplate)

export const Head = ({ data, location }) => {
  const pageData = data.prismicPage.data
  return <Seo pathname={location.pathname} title={pageData.meta_title ? pageData.meta_title : pageData.title} description={pageData.meta_description} image={pageData.page_image.url} />
}
